import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';
import {
  classNames,
  getWeekdayInitials,
  localeNormalizer,
} from '@youshift/shared/utils';
import { useTranslation } from 'react-i18next';
import { SupportedLocale } from '@youshift/shared/types';

import { Month, Day } from '../types';
import { generateMonths } from '../helpers';
import i18n from '../../../utils/i18n';

interface YearlyCalendarProps {
  startDate: Date;
  endDate: Date;
  selectedDates: string[];
  onDateClick: (date: string) => void;
  locale?: SupportedLocale;
}

export default function YearlyCalendar({
  startDate,
  endDate,
  selectedDates,
  onDateClick,
}: YearlyCalendarProps) {
  const { t } = useTranslation();
  const locale = localeNormalizer(i18n.language);
  const [currentYear, setCurrentYear] = useState(
    new Date(startDate).getUTCFullYear(),
  );

  const months = generateMonths(currentYear, startDate, endDate, t, locale);
  const weekdayInitials = getWeekdayInitials(t, locale);

  return (
    <div>
      <header className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
        <h1 className="text-base font-semibold text-gray-900">
          <time dateTime={currentYear.toString()}>{currentYear}</time>
        </h1>
        <div className="flex items-center">
          <div className="relative flex items-center rounded-md bg-white shadow-sm md:items-stretch">
            <button
              type="button"
              onClick={() => setCurrentYear(prev => prev - 1)}
              disabled={currentYear <= startDate.getFullYear()}
              className={classNames(
                'flex h-9 w-12 items-center justify-center rounded-l-md border-y border-l border-gray-300 pr-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pr-0 md:hover:bg-gray-50',
                currentYear <= startDate.getFullYear() &&
                  'cursor-not-allowed opacity-50',
              )}
            >
              <span className="sr-only">Previous year</span>
              <ChevronLeftIcon className="size-5" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => setCurrentYear(new Date().getFullYear())}
              className="hidden border-y border-gray-300 px-3.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus:relative md:block"
            >
              {t('calendars.today')}
            </button>
            <span className="relative -mx-px h-5 w-px bg-gray-300 md:hidden" />
            <button
              type="button"
              onClick={() => setCurrentYear(prev => prev + 1)}
              disabled={currentYear >= endDate.getFullYear()}
              className={classNames(
                'flex h-9 w-12 items-center justify-center rounded-r-md border-y border-r border-gray-300 pl-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pl-0 md:hover:bg-gray-50',
                currentYear >= endDate.getFullYear() &&
                  'cursor-not-allowed opacity-50',
              )}
            >
              <span className="sr-only">{t('calendars.nextYear')}</span>
              <ChevronRightIcon className="size-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>
      <div className="bg-white">
        <div className="mx-auto grid max-w-3xl grid-cols-1 gap-x-8 gap-y-16 px-4 py-16 sm:grid-cols-2 sm:px-6 xl:max-w-none xl:grid-cols-3 xl:px-8 2xl:grid-cols-4">
          {months.map(month => (
            <section key={month.name} className="text-center">
              <h2 className="text-sm font-semibold text-gray-900">
                {month.name}
              </h2>
              <div className="mt-6 grid grid-cols-7 text-xs/6 text-gray-500">
                {weekdayInitials.map((day, index) => (
                  <div key={index}>{day}</div>
                ))}
              </div>
              <div className="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200">
                {month.days.map((day, dayIdx) => (
                  <button
                    key={day.date}
                    type="button"
                    onClick={() => onDateClick(day.date)}
                    disabled={!day.isCurrentMonth}
                    className={classNames(
                      day.isCurrentMonth
                        ? 'bg-white text-gray-900'
                        : 'bg-gray-50 text-gray-400 cursor-not-allowed',
                      selectedDates.includes(day.date) && day.isCurrentMonth
                        ? 'bg-blue-50 text-blue-600'
                        : '',
                      dayIdx === 0 && 'rounded-tl-lg',
                      dayIdx === 6 && 'rounded-tr-lg',
                      dayIdx === month.days.length - 7 && 'rounded-bl-lg',
                      dayIdx === month.days.length - 1 && 'rounded-br-lg',
                      'py-1.5 hover:bg-gray-100 focus:z-10',
                    )}
                  >
                    <time
                      dateTime={day.date}
                      className={classNames(
                        day.isToday &&
                          'bg-gray-200 font-semibold text-gray-800',
                        selectedDates.includes(day.date) &&
                          day.isCurrentMonth &&
                          'bg-blue-600 font-semibold text-white',
                        'mx-auto flex size-7 items-center justify-center rounded-full',
                      )}
                    >
                      {(() => {
                        const parts = day.date.split('-');
                        const dayNumber = parts[parts.length - 1];
                        return dayNumber ? dayNumber.replace(/^0/, '') : '';
                      })()}
                    </time>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
